import { Icon } from '@idexx-laboratories/spot-react';
import classNames from 'classnames';
import React, { ElementType, useCallback } from 'react';

import { useControlledState } from '@react-stately/utils';
import { forwardRefWithAs } from '../../lib/polymorphic';
import { listVisiblePages } from './paging-utils';

export interface PagingProps {
  variant?: 'default' | 'complex';
  size?: 'small' | 'default' | 'large';
  count: number;
  defaultPage?: number;
  disabled?: boolean;
  currentPage?: number;
  maxVisible?: number;
  onPageChange?: (index: number) => void;
}

export const Paging = forwardRefWithAs<'nav', PagingProps>(
  (
    {
      as,
      size = 'default',
      defaultPage = 0,
      count = 0,
      disabled = false,
      children,
      variant = 'default',
      currentPage,
      onPageChange,
      maxVisible = 7,
      ...props
    },
    ref,
  ) => {
    const [selected, setSelected] = useControlledState<number>(
      currentPage,
      defaultPage,
      onPageChange,
    );

    const handleSelect = useCallback(
      (index: number) => {
        setSelected(index);
        onPageChange?.(index);
      },
      [onPageChange, setSelected],
    );

    const MainPagination = () => {
      const pageNumbers = listVisiblePages(count, selected, maxVisible);
      return (
        <>
          {pageNumbers.map(index => {
            const isSelected = selected === index;
            return index >= 0 ? (
              <button
                disabled={disabled}
                key={index}
                aria-current={isSelected ? 'page' : undefined}
                aria-label={`Page ${index + 1} of ${count}${isSelected ? ' (current page)' : ''}`}
                onClick={() => handleSelect(index)}
                className={classNames('spot-paging__button', {
                  'spot-paging__button--selected': isSelected,
                })}
              >
                {index + 1}
              </button>
            ) : (
              <button
                key={index}
                disabled={disabled}
                className='spot-paging__button spot-paging__button--more'
                aria-label='Page undefined'
              />
            );
          })}
        </>
      );
    };

    const Component: ElementType = as ?? 'nav';
    return (
      <Component
        ref={ref}
        className={classNames('spot-paging', {
          [`spot-paging--${size}`]: size !== 'default',
          'spot-paging--disabled': disabled,
        })}
        role='navigation'
        aria-label='Pagination Navigation'
        {...props}
      >
        {variant === 'complex' ? (
          <button
            disabled={disabled || selected === 0}
            onClick={() => handleSelect(0)}
            aria-label={`First page of ${count}`}
            className={classNames('spot-paging__button spot-paging__button--first', {
              'spot-paging__button--disabled': selected === 0,
            })}
            title='First'
          >
            <Icon title='double-left' />
          </button>
        ) : null}
        <div className='spot-paging__group'>
          <button
            disabled={disabled || selected === 0}
            onClick={() => handleSelect(Math.max(0, selected - 1))}
            aria-label={`Previous page of ${count}`}
            className={classNames('spot-paging__button spot-paging__button--previous', {
              'spot-paging__button--disabled': selected === 0,
            })}
            title='Previous'
          >
            <Icon title='previous' />
          </button>
          {count > 0 ? <MainPagination /> : null}

          <button
            disabled={disabled || selected === count - 1}
            onClick={() => handleSelect((selected + 1) % count)}
            className={classNames('spot-paging__button spot-paging__button--next', {
              'spot-paging__button--disabled': selected === count - 1,
            })}
            title='Next'
          >
            <Icon title='next' />
          </button>
        </div>
        {variant === 'complex' ? (
          <button
            disabled={disabled || selected === count - 1}
            onClick={() => handleSelect(count - 1)}
            aria-label={`Last page of ${count}`}
            className={classNames('spot-paging__button spot-paging__button--last', {
              'spot-paging__button--disabled': selected === count - 1,
            })}
            title='Last'
          >
            <Icon title='double-right' />
          </button>
        ) : null}
        {children}
      </Component>
    );
  },
);
